import cn from 'classnames'
import { useEffect, useState } from 'react'
import { last } from 'ramda'

import { useDishTechComponentsState } from './useDishTechComponentsState'
import { generateId } from 'app/common/lib/id'
import PlainButton from 'app/common/ui-next/plain-button'
import { PlainInputNumber } from 'app/common/ui-next/plain-input'
import PlainLabel from 'app/common/ui-next/plain-label'
import { Dish } from 'app/dataTypes/dishes/dish'

import { Field } from './types'
import { useTotalsState } from './useTotalsState'
import { AddRawItemsModal } from '../AddRawItemsModal/AddRawItemsModal'
import { DishTechComponentsTableRow } from './DishTechComponentsTableRow'
import styles from './DishTechComponentsTable.module.scss'

const defaultState = [
  {
    id: generateId(),
    dish: null as Dish | null,
    gross: '',
    net: '',
  },
]

export const DishTechComponentsTable = () => {
  const { dishTechItems, updateDishTechItems } = useDishTechComponentsState({
    defaultItems: defaultState,
  })

  const changeDishTechItem = (id: string, props: Partial<(typeof defaultState)[number]>) => {
    updateDishTechItems((items) =>
      items.map((item) => (item.id === id ? { ...item, ...props } : item)),
    )
  }

  const addDishTechItem = () => {
    updateDishTechItems((items) => [
      ...items,
      {
        id: generateId(),
        dish: null,
        gross: '',
        net: '',
      },
    ])
  }

  const addManyDishTechItems = (dishes: Array<Dish>) => {
    updateDishTechItems((items) => [
      ...items.filter((item) => !item.dish),
      ...dishes.map((dish) => ({
        id: generateId(),
        dish,
        gross: '',
        net: '',
      })),
    ])
  }

  const removeDishTechItem = (id: string) => {
    updateDishTechItems((items) => items.filter((item) => item.id !== id))
  }

  useEffect(() => {
    if (last(dishTechItems)?.dish) {
      addDishTechItem()
    }
  }, [dishTechItems]) // eslint-disable-line react-hooks/exhaustive-deps

  const [showRawItemsModal, setShowRawItemsModal] = useState(false)

  const {
    totalGross,
    totalNet,
    setTotalGrossByUser,
    setTotalNetByUser,
    handleGrossBlur,
    handleNetBlur,
  } = useTotalsState({
    dishTechItems,
  })

  return (
    <div className={styles.root} role="table" aria-label="Параметры компонентов блюда">
      <div className={cn(styles.row, styles.header)} role="row">
        <div className={styles.cell} role="columnheader">
          {'Наименование сырья*'}
        </div>
        <div className={styles.cell} role="columnheader">
          {'Брутто, г*'}
        </div>
        <div className={styles.cell} role="columnheader">
          {'Нетто, г*'}
        </div>
        <div className={styles.cell} role="columnheader" aria-label="Действия"></div>
      </div>

      <div className={styles.body}>
        {dishTechItems.map(({ id, dish, gross, net }, index) => (
          <DishTechComponentsTableRow
            key={id}
            id={id}
            index={index}
            className={styles.row}
            cellClassName={styles.cell}
            inputClassName={styles.input}
            dish={dish}
            gross={gross}
            net={net}
            onDelete={() => removeDishTechItem(id)}
            onChangeDish={(dish) => changeDishTechItem(id, { dish: dish || null })}
            onChangeGross={(gross) => changeDishTechItem(id, { gross })}
            onChangeNet={(net) => changeDishTechItem(id, { net })}
          />
        ))}
      </div>

      <div className={cn(styles.row, styles.footer)}>
        <div className={styles.cell}>
          <PlainButton onClick={() => setShowRawItemsModal(true)}>{'+ Добавить'}</PlainButton>
          {showRawItemsModal && (
            <AddRawItemsModal
              onClose={() => setShowRawItemsModal(false)}
              onAddRawItems={addManyDishTechItems}
            />
          )}
        </div>
        <div className={cn(styles.cell)} role="cell">
          <PlainLabel htmlFor={Field.TotalGross} skipUpperSpace skipBottomSpace>
            {'Выход:'}
          </PlainLabel>
          <PlainInputNumber
            id={Field.TotalGross}
            name={Field.TotalGross}
            className={styles.input}
            value={totalGross.replace(',', '.')}
            onChange={(e) => setTotalGrossByUser(e.target.value)}
            onBlur={handleGrossBlur}
            step="0.01"
            wide
          />
        </div>
        <div className={styles.cell} role="cell">
          <PlainInputNumber
            id={Field.TotalNet}
            name={Field.TotalNet}
            className={styles.input}
            value={totalNet.replace(',', '.')}
            onChange={(e) => setTotalNetByUser(e.target.value)}
            onBlur={handleNetBlur}
            step="0.01"
            wide
          />
        </div>
      </div>
    </div>
  )
}
